import _ from 'lodash';
import { connect } from 'react-redux';
import { classesSelector, specsSelector } from '../../../../../../../../shared/frontend/scripts/selectors/global';
import DefaultSpecCombinedSlot from './DefaultSpecCombinedSlot';
import DefaultRoleCombinedSlot from './DefaultRoleCombinedSlot';
import DefaultSlotCombinedContainer from './DefaultSlotCombinedContainer';
import { arrayFromSet } from '../../../../../../../../shared/common/utils';

type TOwnProps = {
  specComponent?: any;
  roleComponent?: any;
  containerComponent?: any;
  slots: any[];
};

type TPropsFromState = {
  classes: any[];
  specs: any[];
};

type TProfileCombinedSlotsPreviewProps = TOwnProps & TPropsFromState;

const ProfileCombinedSlotsPreviewBase = (props: TProfileCombinedSlotsPreviewProps) => {
  const {
    slots,
    specComponent: SpecSlotComponent = DefaultSpecCombinedSlot,
    roleComponent: RoleSlotComponent = DefaultRoleCombinedSlot,
    containerComponent: SlotContainerComponent = DefaultSlotCombinedContainer
  } = props;

  if (slots.length === 0) {
    return null;
  }

  const slotComponents = [];

  const specsById = _.groupBy(props.specs, 'id');
  const classesById = _.groupBy(props.classes, 'id');

  // the unique set of specs being used per class
  const specsByClass: { [key: string]: { cls: any; mainSpecs: Set<number> } } = {};
  const roles: any[] = [];

  const specComponents: any = [];
  const specPriorities: any = new Map<number, number>();
  const rolePriorities: any = {};

  for (const slot of slots) {
    for (const entry of slot.entries) {
      if (entry.class_id) {
        const cls = _.first(classesById[entry.class_id]);
        if (!cls) {
          continue;
        }

        const mainSpecs = _.compact([
          entry.mainspec_0_id,
          entry.mainspec_1_id,
          entry.mainspec_2_id,
          entry.mainspec_3_id
        ]);

        if (!specsByClass[entry.class_id]) {
          specsByClass[entry.class_id] = {
            cls,
            mainSpecs: new Set<number>()
          };
        }

        for (const specId of mainSpecs) {
          if (!specPriorities.has(specId) || slot.priority > specPriorities.get(specId)) {
            specPriorities.set(specId, slot.priority);
          }
        }

        for (const specId of mainSpecs) {
          specsByClass[entry.class_id].mainSpecs.add(specId);
        }
      } else if (entry.role) {
        roles.push(entry.role);
        if (!rolePriorities[entry.role] || slot.priority > rolePriorities[entry.role]) {
          rolePriorities[entry.role] = slot.priority;
        }
      }
    }
  }

  function getSpecPriorities(cls: any, specs: number[]) {
    const basePriorities = specs.reduce(
      (accum: any, specId) => {
        const pri = specPriorities.get(specId);
        if (pri === 2) {
          accum.high++;
        } else if (pri === 1) {
          accum.normal++;
        } else if (pri === 0) {
          accum.low++;
        }
        return accum;
      },
      {
        high: 0,
        normal: 0,
        low: 0
      }
    );

    // when something is at max ignore the actual count
    let maxSpecCount = 3; // default each thing has 3 specs
    if (cls.slug === 'druid') {
      maxSpecCount = 4;
    } else if (cls.slug === 'demon-hunter') {
      maxSpecCount = 2;
    }

    // normalize the priority counts based on # of specs for the class
    // so that classes like druids and demon hunters sort relatively to
    // each other better
    basePriorities.high /= maxSpecCount;
    basePriorities.normal /= maxSpecCount;
    basePriorities.low /= maxSpecCount;

    return basePriorities;
  }

  const chosenClasses = Object.keys(specsByClass)
    .map((clsId: string) => classesById[clsId][0])
    .sort((a: any, b: any) => {
      // sort classes such that ones with more higher priority items
      // get sorted closer to the front
      const aPriorities = getSpecPriorities(specsByClass[a.id].cls, arrayFromSet<number>(specsByClass[a.id].mainSpecs));
      const bPriorities = getSpecPriorities(specsByClass[b.id].cls, arrayFromSet<number>(specsByClass[b.id].mainSpecs));

      if (aPriorities.high === bPriorities.high) {
        if (aPriorities.normal === bPriorities.normal) {
          if (aPriorities.low === bPriorities.low) {
            return a.name.localeCompare(b.name);
          } else {
            return bPriorities.low - aPriorities.low;
          }
        } else {
          return bPriorities.normal - aPriorities.normal;
        }
      } else {
        return bPriorities.high - aPriorities.high;
      }
    });

  // process the classes now, so we are showing it in a more compact fashion
  for (const cls of chosenClasses) {
    const classData = specsByClass[cls.id];
    const uniqueSpecs = _.compact<any>(
      arrayFromSet<number>(classData.mainSpecs)
        .sort()
        .map((specId: number) => _.first(specsById[specId]))
    );

    specComponents.push(
      <SpecSlotComponent
        key={`spec${specComponents.length}`}
        cls={classData.cls}
        specs={uniqueSpecs}
        specPriorities={specPriorities}
      />
    );
  }

  const roleComponents = _.uniq<string>(roles.sort()).map((role: string) => (
    <RoleSlotComponent key={role} role={role} rolePriorities={rolePriorities} />
  ));

  if (roleComponents.length > 0 || specComponents.length > 0) {
    slotComponents.push(
      <SlotContainerComponent
        key={`slot${slotComponents.length}`}
        roleComponents={roleComponents}
        specComponents={specComponents}
      />
    );
  }

  return <>{slotComponents}</>;
};

function mapStateToProps(state: any) {
  return {
    // this stuff would be a good use case to create a context for
    classes: classesSelector(state),
    specs: specsSelector(state)
  };
}

export const ProfileCombinedSlotsPreview = connect<TPropsFromState>(mapStateToProps)(ProfileCombinedSlotsPreviewBase);
